import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2, Zap, RefreshCw, Monitor, Database } from 'lucide-react';
import { useClientCache } from '@/hooks/use-client-cache';
import { CacheAnalytics } from './CacheAnalytics';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { TopicType } from '@/types/assistant';

export const CacheManagement = () => {
  const { 
    clearCache, 
    cleanupCache, 
    optimizeCache, 
    invalidateByTopic, 
    invalidateByLanguage,
    invalidateByKnowledge,
    cacheEvents
  } = useClientCache();
  
  const [selectedTopic, setSelectedTopic] = useState<string>("guide");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
  const [knowledgeId, setKnowledgeId] = useState<string>("");
  const [activeTab, setActiveTab] = useState("general");

  const handleClearCache = async () => {
    if (confirm('Are you sure you want to clear all caches? This cannot be undone.')) {
      const result = await clearCache();
      if (result) {
        toast.success('All caches cleared successfully');
      } else {
        toast.error('Failed to clear caches');
      }
    }
  };
  
  const handleCleanupCache = async () => {
    toast.loading('Cleaning up expired cache items...');
    await cleanupCache();
    toast.success('Cache cleanup completed');
  };
  
  const handleOptimizeCache = async () => {
    toast.loading('Optimizing cache...');
    await optimizeCache();
    toast.success('Cache optimization completed');
  };
  
  const handleInvalidateByTopic = async () => {
    if (!selectedTopic) {
      toast.error('Please select a topic');
      return;
    }
    
    toast.loading(`Invalidating cache for topic ${selectedTopic}...`);
    const count = await invalidateByTopic(selectedTopic);
    toast.success(`Invalidated ${count} cache entries for topic ${selectedTopic}`);
  };
  
  const handleInvalidateByLanguage = async () => {
    if (!selectedLanguage) {
      toast.error('Please select a language');
      return;
    }
    
    toast.loading(`Invalidating cache for language ${selectedLanguage}...`);
    const count = await invalidateByLanguage(selectedLanguage);
    toast.success(`Invalidated ${count} cache entries for language ${selectedLanguage}`);
  };
  
  const handleInvalidateByKnowledge = async () => {
    if (!knowledgeId) {
      toast.error('Please enter a knowledge ID');
      return;
    }
    
    toast.loading(`Invalidating cache for knowledge ID ${knowledgeId}...`);
    const count = await invalidateByKnowledge(knowledgeId);
    toast.success(`Invalidated ${count} cache entries for knowledge ID ${knowledgeId}`);
  };

  // Topic display names mapping
  const topicDisplayNames: Record<TopicType, string> = {
    guide: "Guide",
    performance: "Performance",
    trading_strategy: "Trading Strategy",
    advisor: "Advisor"
  };

  return (
    <div className="space-y-8">
      {/* Cache Analytics Section */}
      <CacheAnalytics />
      
      {/* Cache Management Section */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle>Cache Management</CardTitle>
          <CardDescription>
            Clear different caches or optimize their performance
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="advanced">Advanced Invalidation</TabsTrigger>
              <TabsTrigger value="events">Cache Events</TabsTrigger>
            </TabsList>
            
            <TabsContent value="general" className="space-y-4">
              <p className="mb-4">
                The assistant uses various caches to improve performance. You can clear these caches
                if you experience issues or want to free up storage space.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted p-4 rounded-md">
                  <h3 className="text-sm font-medium mb-1">Embedding Cache</h3>
                  <p className="text-xs text-muted-foreground mb-2">
                    Stores text embeddings to speed up semantic search and reduce API costs.
                    Clearing this may slow down responses temporarily.
                  </p>
                  <Button variant="outline" size="sm" className="w-full" onClick={handleClearCache}>
                    Clear Embedding Cache
                  </Button>
                </div>
                
                <div className="bg-muted p-4 rounded-md">
                  <h3 className="text-sm font-medium mb-1">Response Cache</h3>
                  <p className="text-xs text-muted-foreground mb-2">
                    Stores AI responses for common questions to improve response times.
                    Clearing this will force new AI requests for previously answered questions.
                  </p>
                  <Button variant="outline" size="sm" className="w-full" onClick={handleClearCache}>
                    Clear Response Cache
                  </Button>
                </div>
                
                <div className="bg-muted p-4 rounded-md">
                  <h3 className="text-sm font-medium mb-1">Message History Cache</h3>
                  <p className="text-xs text-muted-foreground mb-2">
                    Stores messages for offline access and faster loading.
                    Clearing this may affect offline availability of recent messages.
                  </p>
                  <Button variant="outline" size="sm" className="w-full" onClick={handleClearCache}>
                    Clear Message History Cache
                  </Button>
                </div>
                
                <div className="bg-muted p-4 rounded-md">
                  <h3 className="text-sm font-medium mb-1">Cache Optimization</h3>
                  <p className="text-xs text-muted-foreground mb-2">
                    Run cache optimization to improve performance and remove expired items.
                    This is normally done automatically every 6 hours.
                  </p>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={handleCleanupCache}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" /> 
                      Cleanup
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={handleOptimizeCache}
                    >
                      <Zap className="w-4 h-4 mr-1" /> 
                      Optimize
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="advanced" className="space-y-4">
              <p className="text-sm mb-4">
                Advanced cache invalidation options allow you to clear specific parts of the cache based on topics,
                languages, or knowledge base updates.
              </p>
              
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-md">
                  <h3 className="text-sm font-medium mb-2">Invalidate by Topic</h3>
                  <div className="flex gap-2">
                    <Select 
                      value={selectedTopic} 
                      onValueChange={setSelectedTopic}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select topic" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="guide">Guide</SelectItem>
                        <SelectItem value="performance">Performance</SelectItem>
                        <SelectItem value="trading_strategy">Trading Strategy</SelectItem>
                        <SelectItem value="advisor">Advisor</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => invalidateByTopic(selectedTopic)}
                      disabled={!selectedTopic}
                    >
                      Invalidate
                    </Button>
                  </div>
                </div>
                
                <div className="bg-muted p-4 rounded-md">
                  <h3 className="text-sm font-medium mb-2">Invalidate by Language</h3>
                  <div className="flex gap-2">
                    <Select
                      value={selectedLanguage}
                      onValueChange={setSelectedLanguage}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="ko">Korean</SelectItem>
                        <SelectItem value="ja">Japanese</SelectItem>
                        <SelectItem value="zh">Chinese</SelectItem>
                        <SelectItem value="vi">Vietnamese</SelectItem>
                        <SelectItem value="th">Thai</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => invalidateByLanguage(selectedLanguage)}
                      disabled={!selectedLanguage}
                    >
                      Invalidate
                    </Button>
                  </div>
                </div>
                
                <div className="bg-muted p-4 rounded-md">
                  <h3 className="text-sm font-medium mb-2">Invalidate by Knowledge Update</h3>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Enter knowledge ID"
                      value={knowledgeId}
                      onChange={(e) => setKnowledgeId(e.target.value)}
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleInvalidateByKnowledge}
                      disabled={!knowledgeId}
                    >
                      Invalidate
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="events" className="space-y-4">
              <p className="text-sm mb-4">
                Cache events show recent cache invalidation activities and their impact.
              </p>
              
              <div className="border rounded-md">
                <div className="p-3 border-b bg-muted flex">
                  <div className="w-2/3 font-medium">Event</div>
                  <div className="w-1/3 font-medium">Items Affected</div>
                </div>
                <div className="divide-y">
                  {cacheEvents && cacheEvents.length > 0 ? (
                    cacheEvents.map((event, index) => (
                      <div key={index} className="p-3 flex text-sm">
                        <div className="w-2/3">{event.event}</div>
                        <div className="w-1/3">{event.count}</div>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-sm text-center text-muted-foreground">
                      No recent cache events
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
        <CardFooter>
          <Button 
            variant="destructive" 
            className="w-full flex items-center gap-2"
            onClick={handleClearCache}
          >
            <Trash2 className="w-4 h-4" />
            Clear All Caches
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
